import { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Button, Stack } from '@mui/joy';
import { motion } from 'framer-motion';

// Presentational tooltip card for a single tour step.
//
// Positions itself next to the spotlight `targetRect` based on `step.placement`
// ('top' | 'bottom'). Falls back to the opposite side if there isn't enough
// room in the viewport.
//
// For `step.advance === 'button'` → renders a «Далее» (or custom label) button.
// For `step.advance === 'click'`  → renders a hint "Нажми на выделенное" with
// no button, and the OnboardingTour orchestrator listens for the target click.

const TOOLTIP_WIDTH = 340;
const TOOLTIP_MARGIN = 16; // gap between spotlight and tooltip

export default function TourStep({ step, stepIndex, totalSteps, targetRect, onAdvance }) {
    const [viewport, setViewport] = useState(() => ({
        w: window.innerWidth,
        h: window.innerHeight,
    }));

    useEffect(() => {
        const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const tooltipStyle = useMemo(() => {
        if (!targetRect) return null;

        // Horizontal: center on target, clamped to viewport with 16px margin.
        let left = targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2;
        left = Math.max(16, Math.min(left, viewport.w - TOOLTIP_WIDTH - 16));

        // Vertical: place above or below target depending on preference + space.
        const spaceAbove = targetRect.top;
        const spaceBelow = viewport.h - targetRect.bottom;
        const preferred = step.placement || 'bottom';

        // Estimate tooltip height (we don't know it exactly — guess 200px).
        const estimatedHeight = 220;

        let top;
        if (preferred === 'top' && spaceAbove >= estimatedHeight + TOOLTIP_MARGIN) {
            top = targetRect.top - estimatedHeight - TOOLTIP_MARGIN;
        } else if (preferred === 'bottom' && spaceBelow >= estimatedHeight + TOOLTIP_MARGIN) {
            top = targetRect.bottom + TOOLTIP_MARGIN;
        } else if (spaceBelow >= estimatedHeight + TOOLTIP_MARGIN) {
            top = targetRect.bottom + TOOLTIP_MARGIN;
        } else if (spaceAbove >= estimatedHeight + TOOLTIP_MARGIN) {
            top = targetRect.top - estimatedHeight - TOOLTIP_MARGIN;
        } else {
            // Neither side has room — stick to the bottom of the viewport.
            top = viewport.h - estimatedHeight - 24;
        }
        top = Math.max(16, top);

        return {
            position: 'fixed',
            top,
            left,
            width: TOOLTIP_WIDTH,
            zIndex: 10002,
        };
    }, [targetRect, viewport, step.placement]);

    if (!tooltipStyle) return null;

    const isClickStep = step.advance === 'click';
    const buttonLabel = step.buttonLabel || 'Далее';

    return (
        <motion.div
            key={`tour-step-${stepIndex}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.22 }}
            style={tooltipStyle}
        >
            <Box
                sx={{
                    bgcolor: '#FFFFFF',
                    borderRadius: 16,
                    p: 2.5,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.35), 0 0 0 1px rgba(218,119,86,0.2)',
                    border: '1.5px solid #DA7756',
                }}
            >
                {/* Progress row */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                    <Box
                        sx={{
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: '#DA7756',
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                        }}
                    >
                        Шаг {stepIndex + 1} из {totalSteps}
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', gap: 0.5 }}>
                        {Array.from({ length: totalSteps }).map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    flex: 1,
                                    height: 3,
                                    borderRadius: 2,
                                    bgcolor: i <= stepIndex ? '#DA7756' : '#EADFD7',
                                    transition: 'background-color 0.25s',
                                }}
                            />
                        ))}
                    </Box>
                </Stack>

                <Typography
                    level="title-md"
                    sx={{ mb: 0.75, color: '#37352F', fontWeight: 700 }}
                >
                    {step.title}
                </Typography>
                <Typography
                    level="body-sm"
                    sx={{ mb: 2, color: '#5C5A54', lineHeight: 1.55 }}
                >
                    {step.body}
                </Typography>

                {isClickStep ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                            sx={{
                                flex: 1,
                                fontSize: '0.78rem',
                                color: '#DA7756',
                                fontWeight: 600,
                                fontStyle: 'italic',
                            }}
                        >
                            ← Нажми на подсвеченный элемент
                        </Box>
                    </Stack>
                ) : (
                    <Stack direction="row" justifyContent="flex-end">
                        <Button
                            variant="solid"
                            onClick={onAdvance}
                            sx={{
                                bgcolor: step.buttonLabel ? '#37352F' : '#DA7756',
                                color: '#FFFFFF',
                                fontWeight: 600,
                                borderRadius: 10,
                                px: 2.5,
                                '&:hover': { bgcolor: step.buttonLabel ? '#1F1F1F' : '#C4623E' },
                            }}
                        >
                            {buttonLabel}
                        </Button>
                    </Stack>
                )}
            </Box>
        </motion.div>
    );
}
